import {makeStyles} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ClockIcon from '@material-ui/icons/Schedule';
import {usePreferencesState} from "@tracker/shared/src/Preferences/PreferencesProvider";
import {getMinutes, setMinutes} from 'date-fns';
import {FormApi} from 'final-form';
import {Checkboxes, KeyboardDatePicker, TextField} from 'mui-rff';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useField} from "react-final-form";
import {OnChange} from 'react-final-form-listeners';
import {RffSmartKeyboardTimePicker} from '../Input/SmartKeyboardTimePicker';
import {TwoWaySwitch} from "../Input/TwoWaySwitch";
import {useProjects} from "../Tasks/MyProjectsProvider";
import ProjectComponentTaskSelect from "../Tasks/ProjectComponentTaskSelect";
import TaskSelect from '../Tasks/TaskSelect';
import {FormData, required, transformNumber} from "./TimeEntryFormDialog";

const useStyles = makeStyles(theme => ({
    hours: {
        width: 100,
        marginRight: theme.spacing(2),
    },
    minutes: {
        width: 100,
    },
    [theme.breakpoints.up('sm')]: {
        startDatePicker: {
            width: 180,
            marginRight: theme.spacing(2),
        },
        startTimePicker: {
            width: 170,
            marginRight: theme.spacing(2),
        },
        endTimePicker: {
            width: 170,
            marginRight: theme.spacing(2),
        },
        pmTime: {
            paddingBottom: 7,
        },
        minutes: {
            width: 100,
            marginRight: theme.spacing(2),
        },
    },
    timeSwitch: {
        marginLeft: theme.spacing(2),
    },
}));

type Props = {
    initialValues : Partial<FormData>;
    form : FormApi<FormData>;
    modifiedSinceLastSubmit : boolean;
    submitting : boolean;
    enterDuration : boolean;
    setEnterDuration : (enterDuration : boolean) => void;
};

const TimeEntryForm : React.FC<Props> = ({initialValues, form, modifiedSinceLastSubmit, submitting, enterDuration, setEnterDuration} : Props) =>{
    const {myProjects} = useProjects();
    const classes = useStyles();
    const [enterProjectComponent, setEnterProjectComponent] = useState(true);
    const {i18n, preferences} = usePreferencesState();
    const noteRef = useRef<HTMLDivElement | null>(null);
    const projectField = useField('project');
    const taskField = useField('task');

    const isPmForProject = useMemo(() => {
        const projectId = taskField.input.value?.projectId ?? projectField.input.value?.id;
        if (!projectId) {
            return false;
        }

        const myProject = myProjects.find(p => p.id === projectId);
        if (!myProject) {
            return false;
        }

        return myProject.isPmRole && myProject.pmFixedBilling;
    }, [myProjects, projectField.input.value, taskField.input.value.projectId]);

    const roundTimeOnChange = (
        fieldName : keyof FormData,
        form : FormApi<FormData>
    ) => (value : Date | string | null) => {
        if (value === null || typeof value === 'string' || isNaN(value.valueOf())) {
            return;
        }

        const {roundToNearest} = preferences.timeEntry;
        const minutes = getMinutes(value);

        if (minutes % roundToNearest === 0) {
            return;
        }

        form.change(fieldName, setMinutes(value, Math.round(
            getMinutes(value) / roundToNearest
        ) * roundToNearest));
    };

    const roundMinutesOnBlur = (form : FormApi<FormData>) => () => {
        const minutesState = form.getFieldState('minutes');

        if (!minutesState) {
            return;
        }

        const minutesValue = minutesState.value;

        if (!minutesValue) {
            return;
        }

        let minutes = transformNumber(minutesValue);
        const {roundToNearest} = preferences.timeEntry;

        if (isNaN(minutes) || minutes % roundToNearest === 0) {
            return;
        }

        minutes = Math.round(minutes / roundToNearest) * roundToNearest;

        form.batch(() => {
            if (minutes < 60) {
                form.change('minutes', minutes);
                return;
            }

            form.change('minutes', 0);
            const hoursState = form.getFieldState('hours');

            if (!hoursState) {
                return;
            }

            const hoursValue = hoursState.value;

            if (!hoursValue) {
                form.change('hours', 1);
                return;
            }

            const hours = transformNumber(hoursValue);

            if (isNaN(hours)) {
                return;
            }

            form.change('hours', hours + 1);
        });
    };

    const roundHoursOnBlur = (form : FormApi<FormData>) => () => {
        const hoursState = form.getFieldState('hours');

        if (!hoursState) {
            return;
        }

        const hoursValue = hoursState.value;

        if (!hoursValue) {
            return;
        }

        const hours = transformNumber(hoursValue);

        if (Number.isInteger(hours)) {
            return;
        }

        form.batch(() => {
            const flooredHours = Math.floor(hours);
            const minutes = (hours - flooredHours) * 60;

            form.change('hours', flooredHours);
            form.change('minutes', minutes);
        });

        roundMinutesOnBlur(form)();
    };

    useEffect(() => {
        if (initialValues.project && !initialValues.task) {
            return setEnterProjectComponent(true);
        }

        setEnterProjectComponent(preferences.timeEntry.enterProjectAndComponent);
    }, [initialValues, preferences.timeEntry.enterProjectAndComponent]);

    const setNoteFocus = useCallback(() => {
        if (noteRef.current && noteRef.current.focus) {
            noteRef.current.focus();
        }
    }, []);

    return (<>
        <DialogContent>
            <Grid container direction="column">
                <Grid item container direction="row" alignItems="flex-end">
                    <Grid item xs={12} sm="auto">
                        <KeyboardDatePicker
                            KeyboardButtonProps={{tabIndex: -1}}
                            inputVariant="outlined"
                            className={classes.startDatePicker}
                            format={i18n.inputDateFormat}
                            label="Start date"
                            name="startDate"
                            required={required.startDate}
                            autoOk
                            clearable={!required.startDate}
                            disableToolbar={false}
                            variant="dialog"
                            showTodayButton
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <RffSmartKeyboardTimePicker
                            autoFocus={Boolean(initialValues.startDate && !initialValues.startTime)}
                            KeyboardButtonProps={{tabIndex: -1}}
                            inputVariant="outlined"
                            className={classes.startTimePicker}
                            label="Start time"
                            name="startTime"
                            ampm={preferences.locale.useAmPmTime}
                            required={required.startTime}
                            autoOk
                            clearable={!required.startTime}
                            minutesStep={preferences.timeEntry.roundToNearest}
                            keyboardIcon={<ClockIcon/>}
                            showTodayButton
                            todayLabel="Now"
                        />
                    </Grid>
                    <OnChange name="startTime">
                        {roundTimeOnChange('startTime', form)}
                    </OnChange>
                    {enterDuration ? (
                        <>
                            <Grid item>
                                <TextField
                                    autoFocus={Boolean(
                                        initialValues.startDate
                                        && initialValues.startTime
                                        && !initialValues.hours
                                    )}
                                    variant="outlined"
                                    className={classes.hours}
                                    label="Hours"
                                    name="hours"
                                    required={required.hours}
                                    onBlur={roundHoursOnBlur(form)}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    variant="outlined"
                                    className={classes.minutes}
                                    label="Minutes"
                                    name="minutes"
                                    required={required.minutes}
                                    onBlur={roundMinutesOnBlur(form)}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sm="auto">
                                <RffSmartKeyboardTimePicker
                                    autoFocus={Boolean(
                                        initialValues.startDate
                                        && initialValues.startTime
                                        && !initialValues.endTime
                                    )}
                                    KeyboardButtonProps={{tabIndex: -1}}
                                    inputVariant="outlined"
                                    className={classes.endTimePicker}
                                    label="End time"
                                    name="endTime"
                                    ampm={preferences.locale.useAmPmTime}
                                    required={required.endTime}
                                    autoOk
                                    clearable={!required.endTime}
                                    minutesStep={preferences.timeEntry.roundToNearest}
                                    keyboardIcon={<ClockIcon/>}
                                    showTodayButton
                                    todayLabel="Now"
                                />
                            </Grid>
                            <OnChange name="endTime">
                                {roundTimeOnChange('endTime', form)}
                            </OnChange>
                        </>
                    )}
                    {isPmForProject && <Grid item xs={12} sm="auto" className={classes.pmTime}>
                        <Checkboxes
                            color="primary"
                            name="isPMTimeEntry"
                            data={{
                                label: 'PM Time',
                                value: true,
                            }}/>
                    </Grid>}
                </Grid>
                {enterProjectComponent
                    ? <ProjectComponentTaskSelect
                        setNoteFocus={setNoteFocus}
                    />
                    : <Grid item>
                        <TaskSelect
                            label="Task"
                            name="task"
                            required={false}
                            taskHierarchy={false}
                            setNoteFocus={setNoteFocus}
                        />
                    </Grid>
                }
                <Grid item>
                    <TextField
                        variant="outlined"
                        label="Note"
                        name="note"
                        required={required.note}
                        inputRef={noteRef}
                    />
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Grid container>
                <Grid item>
                    <Typography component="div" className={classes.timeSwitch}>
                        <Grid component="label" container alignItems="center" spacing={3}>
                            <Grid item>
                                Range
                                <TwoWaySwitch
                                    color="primary"
                                    checked={enterDuration}
                                    onChange={event => setEnterDuration(event.target.checked)}
                                />
                                Duration
                            </Grid>
                            <Grid item>
                                <Tooltip title="Shows the Project and Component selectors" arrow>
                                    <Typography component="div" className={classes.timeSwitch}>
                                        <Grid component="label" container alignItems="center">
                                            <Grid item>Search</Grid>
                                            <Grid item>
                                                <TwoWaySwitch
                                                    color="primary"
                                                    checked={enterProjectComponent}
                                                    onChange={event => setEnterProjectComponent(event.target.checked)}
                                                />
                                            </Grid>
                                            <Grid item>Hierarchy</Grid>
                                        </Grid>
                                    </Typography>
                                </Tooltip>

                            </Grid>
                        </Grid>
                    </Typography>
                </Grid>
                <Grid item xs={true}/>
                <Grid>
                    <Button type="submit" color="primary" disabled={modifiedSinceLastSubmit || submitting}>
                        Save
                    </Button>
                </Grid>
            </Grid>
        </DialogActions>
    </>);
};

export default TimeEntryForm;
